import { PartnerDocument } from '@/api/PartnerDocument';
import ConfirmDialog from '@/components/ConfirmDialog';
import ErrorMessage from '@/components/ErrorMessage';
import CancelButton from '@/components/buttons/CancelButton';
import SubmitButton from '@/components/buttons/SubmitButton';
import { Button } from '@/components/ui/button';
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { formatPicker } from '@/data/DateTimeUtil';
import { LabelAndValueOption } from '@/data/LabelAndValueOption';
import { PartnerDocumentListFilters, partnerDocumentService } from '@/service/PartnerDocumentService';
import { Box, FileUpload, Heading, HStack, Icon, IconButton, SimpleGrid, Spinner, Stack, Table, Text, Textarea } from '@chakra-ui/react';
import { Select as ReactSelect } from 'chakra-react-select';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LuDownload, LuFileArchive, LuPencil, LuTrash2, LuUpload } from 'react-icons/lu';
import { useI18n } from 'src/i18n/I18nContext';

interface PartnerDocumentsPanelProps {
    filters?: PartnerDocumentListFilters;
    canManage?: boolean;
    isAvailable?: boolean;
    showSummary?: boolean;
}

interface PartnerDocumentEditDialogProps {
    document: PartnerDocument | null;
    projectOptions: LabelAndValueOption[];
    onClose: () => void;
    onConfirm: (document: PartnerDocument, projectId: number, comments: string) => Promise<void>;
}

const formatFileSize = (value: number): string => {
    if (value < 1024) {
        return `${value} B`;
    }

    if (value < 1024 * 1024) {
        return `${(value / 1024).toFixed(1)} KB`;
    }

    return `${(value / 1024 / 1024).toFixed(1)} MB`;
};

const downloadBlob = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.setAttribute('download', fileName);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
};

const PartnerDocumentEditDialog = ({ document, projectOptions, onClose, onConfirm }: PartnerDocumentEditDialogProps) => {
    const { t } = useI18n();
    const [projectId, setProjectId] = useState<number | null>(null);
    const [comments, setComments] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setProjectId(document?.projectId ?? null);
        setComments(document?.comments ?? '');
        setError('');
        setIsSubmitting(false);
    }, [document]);

    const handleSubmit = () => {
        if (!document || !projectId) {
            return;
        }

        setIsSubmitting(true);
        setError('');
        onConfirm(document, projectId, comments)
            .then(() => onClose())
            .catch(err => setError(err?.message ?? t('landing.partner.saveError')))
            .finally(() => setIsSubmitting(false));
    };

    return (
        <DialogRoot open={!!document} onOpenChange={() => onClose()} placement='center' size='md' motionPreset='scale'>
            <DialogContent>
                <DialogHeader>{t('landing.partner.editDocument')}</DialogHeader>
                <DialogCloseTrigger />
                <DialogBody>
                    <Stack gap='4'>
                        {error && <ErrorMessage message={error} />}
                        <Field label={t('landing.partner.project')} required>
                            <ReactSelect<LabelAndValueOption, false>
                                isSearchable
                                required
                                placeholder={t('landing.partner.projectPlaceholder')}
                                options={projectOptions}
                                onChange={val => setProjectId(val?.value ?? null)}
                                value={projectOptions.find(project => project.value === projectId) ?? null}
                            />
                        </Field>
                        <Field label={t('landing.partner.comment')}>
                            <Textarea value={comments} onChange={ev => setComments(ev.target.value)} minH='7rem' resize='vertical' />
                        </Field>
                    </Stack>
                </DialogBody>
                <DialogFooter>
                    <CancelButton onClick={onClose} />
                    <SubmitButton
                        isSubmitting={isSubmitting}
                        disabled={!projectId}
                        onClick={handleSubmit}
                    />
                </DialogFooter>
            </DialogContent>
        </DialogRoot>
    );
};

const PartnerDocumentsPanel = ({ filters, canManage = false, isAvailable = true, showSummary = false }: PartnerDocumentsPanelProps) => {
    const { t } = useI18n();
    const [documents, setDocuments] = useState<PartnerDocument[]>([]);
    const [projects, setProjects] = useState<LabelAndValueOption[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [comments, setComments] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const [editingDocument, setEditingDocument] = useState<PartnerDocument | null>(null);
    const [deletingDocument, setDeletingDocument] = useState<PartnerDocument | null>(null);
    const [downloadingId, setDownloadingId] = useState<number | null>(null);
    const partnerId = filters?.partnerId;
    const projectId = filters?.projectId;

    const listFilters = useMemo<PartnerDocumentListFilters>(() => {
        const next: PartnerDocumentListFilters = {};
        if (partnerId !== undefined) {
            next.partnerId = partnerId;
        }
        if (projectId !== undefined) {
            next.projectId = projectId;
        }
        return next;
    }, [partnerId, projectId]);

    const hasFilters = partnerId !== undefined || projectId !== undefined;

    const loadDocuments = useCallback((): Promise<void> => {
        if (!isAvailable) {
            setDocuments([]);
            setProjects([]);
            setIsLoading(false);
            return Promise.resolve();
        }

        setIsLoading(true);
        setError('');

        return partnerDocumentService.listAll(hasFilters ? listFilters : undefined)
            .then(response => {
                setDocuments([...response.results]);
                const options = response.projects.map(project => ({
                    label: project.name,
                    value: project.id,
                }));
                setProjects(options);

                setSelectedProjectId(current => current ?? (options.length === 1 ? options[0].value : null));
            })
            .catch(err => setError(err?.message ?? t('landing.partner.loadError')))
            .finally(() => setIsLoading(false));
    }, [hasFilters, isAvailable, listFilters, t]);

    useEffect(() => {
        loadDocuments();
    }, [loadDocuments]);

    const projectNameById = useMemo(() => {
        return new Map(projects.map(project => [project.value, project.label]));
    }, [projects]);

    const lastUpdated = useMemo(() => {
        return documents.reduce<Date | null>((latest, document) => {
            const updatedAt = new Date(document.updatedAt);
            return !latest || updatedAt > latest ? updatedAt : latest;
        }, null);
    }, [documents]);

    const handleUpload = (): Promise<void> => {
        if (!selectedFile || !selectedProjectId) {
            return Promise.resolve();
        }

        setIsUploading(true);
        setError('');

        return partnerDocumentService.upload(selectedFile, selectedProjectId, comments)
            .then(() => {
                setSelectedFile(null);
                setComments('');
                return loadDocuments();
            })
            .catch(err => setError(err?.message ?? t('landing.partner.uploadError')))
            .finally(() => setIsUploading(false));
    };

    const handleUpdate = (document: PartnerDocument, projectId: number, nextComments: string): Promise<void> => {
        return partnerDocumentService.update(document.id, projectId, nextComments)
            .then(loadDocuments);
    };

    const handleDelete = (): Promise<void> => {
        if (!deletingDocument) {
            return Promise.resolve();
        }

        return partnerDocumentService.remove(deletingDocument.id)
            .then(() => {
                setDeletingDocument(null);
                return loadDocuments();
            })
            .catch(err => {
                setError(err?.message ?? t('landing.partner.deleteError'));
            });
    };

    const handleDownload = (document: PartnerDocument): Promise<void> => {
        setDownloadingId(document.id);
        setError('');

        return partnerDocumentService.download(document.id)
            .then(blob => downloadBlob(blob, document.fileName))
            .catch(err => setError(err?.message ?? t('landing.partner.downloadError')))
            .finally(() => setDownloadingId(null));
    };

    const canUpload = canManage && isAvailable && !!selectedFile && !!selectedProjectId && !isUploading;

    return (
        <Stack gap='6'>
            {showSummary && (
                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <Box borderWidth='1px' borderColor='border.subtle' rounded='lg' p='4' bg='bg.panel'>
                        <Text fontSize='sm' color='fg.muted'>
                            {t('landing.partner.documentsCount')}
                        </Text>
                        <Text fontSize='3xl' fontWeight='bold'>
                            {documents.length}
                        </Text>
                    </Box>
                    <Box borderWidth='1px' borderColor='border.subtle' rounded='lg' p='4' bg='bg.panel'>
                        <Text fontSize='sm' color='fg.muted'>
                            {t('landing.partner.lastUpdated')}
                        </Text>
                        <Text fontSize='xl' fontWeight='semibold'>
                            {lastUpdated ? formatPicker(lastUpdated) : '-'}
                        </Text>
                    </Box>
                </SimpleGrid>
            )}

            {error && <ErrorMessage message={error} />}

            <Box borderWidth='1px' borderColor='border.subtle' rounded='lg' bg='bg.panel' overflow='hidden'>
                <Box px='4' py='3' borderBottomWidth='1px' borderColor='border.subtle'>
                    <HStack gap='2'>
                        <LuFileArchive />
                        <Heading as='h2' size='lg'>
                            {t('landing.partner.documentsTitle')}
                        </Heading>
                    </HStack>
                </Box>

                {canManage && (
                    <Box px='0' py='4' borderBottomWidth='1px' borderColor='border.subtle' >
                        <Box borderWidth='1px' borderColor='border.emphasized' rounded='md' bg='bg.panel' p='4' shadow='md'>
                            <SimpleGrid columns={{ base: 1, xl: 2 }} gap='5' alignItems='stretch'>
                                <Stack gap='4' h='full'>
                                    <Field label={t('landing.partner.project')} required>
                                        <ReactSelect<LabelAndValueOption, false>
                                            isSearchable
                                            required
                                            isDisabled={!isAvailable || projects.length === 0}
                                            placeholder={t('landing.partner.projectPlaceholder')}
                                            options={projects}
                                            onChange={val => setSelectedProjectId(val?.value ?? null)}
                                            value={projects.find(project => project.value === selectedProjectId) ?? null}
                                        />
                                    </Field>
                                    <Field label={t('landing.partner.comment')} flex='1' display='flex' minH='0'>
                                        <Textarea
                                            value={comments}
                                            onChange={ev => setComments(ev.target.value)}
                                            disabled={!isAvailable}
                                            flex='1'
                                            minH='7rem'
                                            resize='vertical'
                                        />
                                    </Field>
                                </Stack>
                                <Stack gap='3' h='full' justify='space-between'>
                                    <Field label={t('landing.partner.file')} required>
                                        <FileUpload.Root
                                            alignItems='stretch'
                                            accept={{
                                                'application/pdf': ['.pdf'],
                                                'application/zip': ['.zip'],
                                                'application/x-zip-compressed': ['.zip'],
                                            }}
                                            acceptedFiles={selectedFile ? [selectedFile] : []}
                                            disabled={!isAvailable}
                                            maxFiles={1}
                                            onFileChange={details => setSelectedFile(details.acceptedFiles[0] ?? null)}
                                        >
                                            <FileUpload.HiddenInput data-testid='partner-document-file-input' />
                                            <FileUpload.Dropzone minH='10rem'>
                                                <Icon size='md' color='fg.muted'>
                                                    <LuUpload />
                                                </Icon>
                                                <FileUpload.DropzoneContent>
                                                    <Box>{t('landing.partner.dropzoneTitle')}</Box>
                                                    <Box color='fg.muted'>{t('landing.partner.dropzoneHelp')}</Box>
                                                </FileUpload.DropzoneContent>
                                            </FileUpload.Dropzone>
                                            <FileUpload.List clearable showSize />
                                        </FileUpload.Root>
                                    </Field>
                                    <Button
                                        colorPalette='blue'
                                        rounded='md'
                                        alignSelf={{ base: 'stretch', sm: 'flex-end' }}
                                        loading={isUploading}
                                        disabled={!canUpload}
                                        onClick={handleUpload}
                                    >
                                        <LuUpload /> {t('landing.partner.upload')}
                                    </Button>
                                </Stack>
                            </SimpleGrid>
                        </Box>
                    </Box>
                )}

                <Box overflowX='auto' py='1rem'>
                    <Table.Root size='sm' minW='48rem'>
                        <Table.Header>
                            <Table.Row>
                                <Table.ColumnHeader>{t('landing.partner.fileName')}</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('landing.partner.project')}</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('landing.partner.comment')}</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('landing.partner.updatedAt')}</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign='end'>{t('landing.partner.fileSize')}</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign='end'>{t('landing.partner.actions')}</Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {isLoading && (
                                <Table.Row>
                                    <Table.Cell colSpan={6} py='8' textAlign='center'>
                                        <Spinner size='sm' mr='2' />
                                        <Text as='span' color='fg.muted'>{t('landing.partner.loading')}</Text>
                                    </Table.Cell>
                                </Table.Row>
                            )}
                            {!isLoading && documents.length === 0 && (
                                <Table.Row>
                                    <Table.Cell colSpan={6} py='8' textAlign='center'>
                                        <Text color='fg.muted'>{t('landing.partner.empty')}</Text>
                                    </Table.Cell>
                                </Table.Row>
                            )}
                            {!isLoading && documents.map(document => (
                                <Table.Row key={document.id}>
                                    <Table.Cell fontWeight='medium'>{document.fileName}</Table.Cell>
                                    <Table.Cell>{projectNameById.get(document.projectId) ?? '-'}</Table.Cell>
                                    <Table.Cell maxW='22rem'>
                                        <Text lineClamp={2}>{document.comments || '-'}</Text>
                                    </Table.Cell>
                                    <Table.Cell>{formatPicker(document.updatedAt)}</Table.Cell>
                                    <Table.Cell textAlign='end'>{formatFileSize(document.fileSize)}</Table.Cell>
                                    <Table.Cell textAlign='end'>
                                        <HStack gap='1' justify='flex-end'>
                                            <IconButton
                                                aria-label={t('landing.partner.download')}
                                                size='xs'
                                                variant='ghost'
                                                loading={downloadingId === document.id}
                                                onClick={() => handleDownload(document)}
                                            >
                                                <LuDownload />
                                            </IconButton>
                                            {canManage && (
                                                <>
                                                    <IconButton
                                                        aria-label={t('landing.partner.edit')}
                                                        size='xs'
                                                        variant='ghost'
                                                        onClick={() => setEditingDocument(document)}
                                                    >
                                                        <LuPencil />
                                                    </IconButton>
                                                    <IconButton
                                                        aria-label={t('landing.partner.delete')}
                                                        size='xs'
                                                        variant='ghost'
                                                        colorPalette='red'
                                                        onClick={() => setDeletingDocument(document)}
                                                    >
                                                        <LuTrash2 />
                                                    </IconButton>
                                                </>
                                            )}
                                        </HStack>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                            {!isLoading && canManage && projects.length === 0 && (
                                <Table.Row>
                                    <Table.Cell colSpan={6} py='4' textAlign='center'>
                                        <Text color='fg.muted'>{t('landing.partner.noProjects')}</Text>
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table.Root>
                </Box>
            </Box>

            {canManage && (
                <>
                    <PartnerDocumentEditDialog
                        document={editingDocument}
                        projectOptions={projects}
                        onClose={() => setEditingDocument(null)}
                        onConfirm={handleUpdate}
                    />
                    <ConfirmDialog
                        heading={t('landing.partner.deleteConfirm')}
                        isOpen={!!deletingDocument}
                        onClose={() => setDeletingDocument(null)}
                        onConfirm={handleDelete}
                    />
                </>
            )}
        </Stack>
    );
};

export default PartnerDocumentsPanel;
